import { useIntl } from 'react-intl'

import { ButtonGroup, FormattedMessage, useNavigation } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'
import { boolean, object, optional, refine } from '@subaio/validation'
import { Checkbox, Input } from '@subaio/webapp'

import { asContainer } from 'lib/container'
import { useValidation } from 'lib/useValidation'

import { BottomContent } from 'containers/SignUp/BottomContent'
import { FieldRow } from 'containers/SignUp/FieldRow'
import { Title } from 'containers/SignUp/Title'

import { ButtonSolidSubmit } from 'components/Buttons'
import { CenterContent } from 'components/Layout'
import { useSnackbar } from 'components/Snackbars'

export const container = asContainer<{}>()({
	path: '/sign-up',
	authentication: { type: 'public' },
	Running: ({ api }) => {
		const { onUpdateUrlTo } = useNavigation()
		const { handleSnackbars } = useSnackbar()
		const { fieldEmail, fieldString, passwordsMatchMessage, withLength } = useValidation()
		const { locale } = useIntl()
		const language = locale.split('-')[0]
		const languageSuffix = language === 'en' ? '' : `-${language}`

		return (
			<CenterContent>
				<Form
					schema={object({
						name: withLength(fieldString(), 1, 255),
						email: withLength(fieldEmail(), 1, 320),
						password: withLength(fieldString(), 8, 128),
						passwordConfirm: refine(
							withLength(fieldString(), 8, 128),
							'passwordsMatch',
							(confirm, { branch }) => branch[0].password === confirm || passwordsMatchMessage,
						),
						acceptsMarketing: optional(boolean()),
						consent: refine(boolean(), 'consent', (value) => value === true || 'You must give consent to the solution'),
					})}
					onSubmit={handleSnackbars(
						async ({ passwordConfirm, consent, ...rest }) => {
							if (!consent) throw new Error('User did not consent')
							const { challengeId } = await api.post<{ challengeId: string }>('/auth/signup-private', rest)
							onUpdateUrlTo({ name: 'VerifyEmail', query: { 'challenge-id': challengeId } })
						},
						{ 401: 'Failed to sign up', default: 'Failed to sign up' },
					)}
					render={({ name, email, password, passwordConfirm, acceptsMarketing, consent }) => (
						<>
							<Title>Sign up to Finance Data</Title>
							<FieldRow>
								<Input {...name} label='Full name' autoComplete='full name' />
								<Input {...email} label='Email' type='email' autoComplete='email' />
							</FieldRow>
							<FieldRow>
								<Input {...password} label='Password' type='password' autoComplete='password' />
								<Input {...passwordConfirm} label='Password confirm' type='password' autoComplete='password confirm' />
							</FieldRow>
							<Checkbox
								{...acceptsMarketing}
								label={
									<FormattedMessage
										id='signup.marketingConsent'
										defaultMessage='Yes, I would like Subaio to contact me by email or phone with news, updates and relevant information about Finance Data. I can withdraw my consent at any time.'
									/>
								}
							/>
							<Checkbox
								{...consent}
								label={
									<FormattedMessage
										id='signup.consent'
										defaultMessage='I accept the <termsOfUseLink>Terms of Use</termsOfUseLink> and acknowledge that I have read the <privacyPolicyLink>Privacy Policy</privacyPolicyLink>.'
										values={{
											termsOfUseLink: (node) => (
												<a
													target='_blank'
													href={`https://subaio.com/legal/finance-data/terms-of-use${languageSuffix}`}
													rel='noopener'
												>
													{node}
												</a>
											),
											privacyPolicyLink: (node) => (
												<a
													target='_blank'
													href={`https://subaio.com/legal/finance-data/privacy-policy/personal${languageSuffix}`}
													rel='noopener'
												>
													{node}
												</a>
											),
										}}
									/>
								}
							/>
							<BottomContent>
								<ButtonGroup center>
									<ButtonSolidSubmit label='Sign up' />
								</ButtonGroup>
							</BottomContent>
						</>
					)}
				/>
			</CenterContent>
		)
	},
})
